import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, MessageSquare } from "lucide-react";
import api from "../lib/api";
import { feedbackHref } from "../lib/feedbackLink";
import { Label, InfoBadge } from "./common";
import { periodLabel } from "./PeriodFilter";

const STAR_COLORS = { 1: "#EF4444", 2: "#F97316", 3: "#F59E0B", 4: "#84CC16", 5: "#10B981" };
const pct = (a, b) => (b ? Math.round((100 * a) / b) : 0);

function Box({ title, children, testId }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4" data-testid={testId}>
      <p className="mb-3 font-head text-sm font-bold text-slate-900">{title}</p>
      {children}
    </div>
  );
}

function StarRows({ d, go }) {
  return (
    <div className="space-y-1.5">
      {[5, 4, 3, 2, 1].map((s) => {
        const n = d.distribution[s] || 0;
        return (
          <button key={s} onClick={() => go({ rating: s })} disabled={!n} data-testid={`rs-star-${s}`}
            className="group flex w-full items-center gap-2 rounded-md px-1 py-0.5 text-left text-sm hover:bg-white disabled:cursor-default disabled:hover:bg-transparent">
            <span className="w-7 font-bold" style={{ color: STAR_COLORS[s] }}>{s}★</span>
            <div className="h-2 flex-1 rounded-full bg-slate-200"><div className="h-2 rounded-full" style={{ width: `${pct(n, d.total)}%`, background: STAR_COLORS[s] }} /></div>
            <span className="w-16 text-right text-xs text-slate-500"><b className="text-slate-800">{n}</b> · {pct(n, d.total)}%</span>
          </button>
        );
      })}
    </div>
  );
}

function YesNoRow({ label, yes, no, total, onYes, onNo, testId }) {
  const y = pct(yes, total);
  return (
    <div data-testid={testId}>
      <p className="mb-1 text-sm font-bold text-slate-800">{label}</p>
      <div className="flex h-7 overflow-hidden rounded-md text-xs font-bold">
        {yes > 0 && <button onClick={onYes} className="flex items-center justify-center bg-emerald-500 text-white transition-opacity hover:opacity-85" style={{ width: `${Math.max(y, 14)}%` }} data-testid={`${testId}-yes`}>Yes {y}%</button>}
        {no > 0 && <button onClick={onNo} className="flex flex-1 items-center justify-center bg-rose-500 text-white transition-opacity hover:opacity-85" data-testid={`${testId}-no`}>No {100 - y}%</button>}
      </div>
      <p className="mt-0.5 text-[11px] text-slate-400">{yes} yes · {no} no · {total} answers</p>
    </div>
  );
}

function Chips({ items, onPick, testId, tone = "rose" }) {
  if (!items.length) return <p className="text-sm text-slate-400">None in this period.</p>;
  const cls = tone === "rose" ? "bg-rose-50 text-rose-700 hover:bg-rose-100" : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100";
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {items.map((p) => (
        <button key={p.key || p.label} onClick={() => onPick(p)} data-testid={`${testId}-${(p.key || p.label).replace(/\s+/g, "-").toLowerCase()}`}
          className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${cls}`}>{p.label} · {p.count}</button>
      ))}
    </div>
  );
}

function Dimensions({ items, go }) {
  if (!items.length) return <p className="text-sm text-slate-400">No detail ratings yet. Turn them on in Settings.</p>;
  return (
    <div className="space-y-2">
      {items.map((dm) => (
        <button key={dm.key} onClick={() => go({ dimension: dm.key })} data-testid={`rs-dim-${dm.key}`}
          className="flex w-full items-center gap-2 rounded-md px-1 py-0.5 text-left text-sm hover:bg-white">
          <span className="w-24 truncate font-bold text-slate-800">{dm.label}</span>
          <div className="h-2 flex-1 rounded-full bg-slate-200"><div className="h-2 rounded-full bg-amber-400" style={{ width: `${(dm.avg / 5) * 100}%` }} /></div>
          <span className="w-16 text-right text-xs text-slate-500"><b className="text-slate-800">{dm.avg}</b> ({dm.count})</span>
        </button>
      ))}
    </div>
  );
}

function StarMatrixTable({ d, go, testId }) {
  if (!d.dimensions.length) return null;
  const rows = [
    { key: "__overall__", label: "Overall rating", dist: d.distribution, total: d.total, avg: d.avg_rating ?? "—", bold: true },
    ...d.dimensions.map((dm) => ({ key: dm.key, label: dm.label, dist: dm.dist, total: dm.count, avg: dm.avg })),
  ];
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white" data-testid={`${testId}-matrix`}>
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <p className="font-head text-sm font-bold text-slate-900">Rating detail by criterion</p>
        <span className="text-xs text-slate-400">Click any cell to open the exact ratings behind it</span>
      </div>
      <div className="overflow-x-auto sv-scroll">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-400">
            <tr>
              <th className="px-4 py-2 text-left">Criterion</th>
              {[1,2,3,4,5].map((s) => (
                <th key={s} className="px-2 py-2 text-right" style={{ color: STAR_COLORS[s] }}>{s}★</th>
              ))}
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-right">Avg</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-slate-100">
                <td className={`px-4 py-2 ${r.bold ? "font-head font-bold text-slate-900" : "font-semibold text-slate-800"}`}>{r.label}</td>
                {[1,2,3,4,5].map((s) => {
                  const n = (r.dist && r.dist[String(s)]) || 0;
                  const share = r.total ? Math.round((100 * n) / r.total) : 0;
                  const extra = r.key === "__overall__" ? { rating: s } : { dimension: r.key, rating: s };
                  return (
                    <td key={s} className="px-2 py-2 text-right">
                      <button onClick={() => n && go(extra)} disabled={!n}
                              data-testid={`${testId}-cell-${r.key}-${s}`}
                              className={`inline-flex min-w-[48px] flex-col items-end rounded px-1.5 py-0.5 text-xs transition-colors ${
                                n ? "hover:bg-slate-100" : "cursor-default opacity-40"
                              }`}>
                        <span className="sv-metric font-bold text-slate-900">{n}</span>
                        {n > 0 && <span className="text-[10px] text-slate-400">{share}%</span>}
                      </button>
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-right text-slate-600">{r.total || 0}</td>
                <td className="px-3 py-2 text-right">
                  <span className="sv-metric font-bold" style={{
                    color: typeof r.avg === "number" ? (r.avg >= 4.5 ? "#10B981" : r.avg >= 3.5 ? "#F59E0B" : "#EF4444") : "#64748B"
                  }}>{r.avg}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ResponsesSummary({ locationId, employeeId, days, title = "Ratings & responses summary", testId = "responses-summary" }) {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  const scope = { location_id: locationId, employee_id: employeeId, days };

  useEffect(() => {
    setD(null);
    const q = new URLSearchParams(Object.entries({ location_id: locationId, employee_id: employeeId, days }).filter(([, v]) => v !== undefined && v !== null && v !== "")).toString();
    api.get(`/responses/summary${q ? `?${q}` : ""}`).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [locationId, employeeId, days]);

  const go = (extra) => navigate(feedbackHref({ ...scope, ...extra }));

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testId}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5"><Label>{title}{days !== undefined && days !== null && days !== "" ? ` · ${periodLabel(days)}` : ""}</Label>
          <InfoBadge title="Ratings & responses" testId={`${testId}-info`}
            text="Everything customers told you: star ratings, answers to your Yes/No questions, detail ratings, what went wrong and comment themes. Click any bar, answer or chip to see the exact ratings behind it." /></span>
        {d?.total > 0 && (
          <button onClick={() => go({})} data-testid={`${testId}-view-all`} className="inline-flex items-center gap-1 text-sm font-bold text-indigo-700 hover:text-indigo-900">
            View all {d.total} ratings <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
      {!d ? <p className="py-8 text-center text-sm text-slate-400">Loading responses…</p>
        : d.error ? <p className="py-8 text-center text-sm text-slate-400">Could not load responses.</p>
        : d.total === 0 ? <p className="py-8 text-center text-sm text-slate-400">No ratings in this period yet.</p>
        : <>
            <SummaryGrid d={d} go={go} testId={testId} />
            <StarMatrixTable d={d} go={go} testId={testId} />
          </>}
    </div>
  );
}

function SummaryGrid({ d, go, testId }) {
  return (
    <div className="grid items-start gap-4 md:grid-cols-2 xl:grid-cols-3">
      <Box title={`Star ratings · avg ${d.avg_rating}`} testId={`${testId}-stars`}>
        <StarRows d={d} go={go} />
        <div className="mt-3 flex flex-wrap gap-3 border-t border-slate-200 pt-2 text-xs text-slate-500">
          <button onClick={() => go({ has_comment: "true" })} className="inline-flex items-center gap-1 font-semibold hover:text-indigo-700" data-testid={`${testId}-comments`}>
            <MessageSquare className="h-3.5 w-3.5" /> {d.comments} with comments</button>
          <button onClick={() => go({ source: "qr" })} className="font-semibold hover:text-indigo-700" data-testid={`${testId}-src-qr`}>QR {d.sources.qr}</button>
          <button onClick={() => go({ source: "nfc" })} className="font-semibold hover:text-indigo-700" data-testid={`${testId}-src-nfc`}>NFC {d.sources.nfc}</button>
        </div>
      </Box>
      <Box title="Question responses" testId={`${testId}-questions`}>
        {d.questions.length === 0 ? <p className="text-sm text-slate-400">No question answers yet.</p> : (
          <div className="space-y-3">
            {d.questions.map((q, i) => (
              <YesNoRow key={q.q} label={q.q} yes={q.yes} no={q.no} total={q.total} testId={`${testId}-q-${i}`}
                onYes={() => go({ question: q.q, answer: "yes" })} onNo={() => go({ question: q.q, answer: "no" })} />
            ))}
          </div>
        )}
      </Box>
      <Box title="Detail ratings (1–5)" testId={`${testId}-dimensions`}><Dimensions items={d.dimensions} go={go} /></Box>
      {d.touchpoints.length > 0 && (
        <Box title="Service moments" testId={`${testId}-touchpoints`}>
          <div className="space-y-3">
            {d.touchpoints.map((t) => (
              <YesNoRow key={t.key} label={t.label} yes={t.yes} no={t.no} total={t.total} testId={`${testId}-tp-${t.key}`}
                onYes={() => go({ touchpoint: t.key, answer: "yes" })} onNo={() => go({ touchpoint: t.key, answer: "no" })} />
            ))}
          </div>
        </Box>
      )}
      <Box title="Low ratings: what went wrong" testId={`${testId}-problems`}>
        <Chips items={d.problems} onPick={(p) => go({ problem_category: p.label })} testId={`${testId}-problem`} />
        {(d.resolved.yes + d.resolved.no) > 0 && (
          <div className="mt-4"><YesNoRow label="Resolved at the time?" yes={d.resolved.yes} no={d.resolved.no} total={d.resolved.yes + d.resolved.no}
            testId={`${testId}-resolved`} onYes={() => go({ resolved: "yes" })} onNo={() => go({ resolved: "no" })} /></div>
        )}
      </Box>
      <Box title="Comment themes" testId={`${testId}-themes`}>
        <Chips items={d.themes} tone="indigo" onPick={(t) => go({ theme: t.key })} testId={`${testId}-theme`} />
      </Box>
    </div>
  );
}
